import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { X, Loader2 } from 'src/components/common/icons';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface IVoidInvoiceDto {
    liTg: number;
    lsNo: number;
    invoiceNo: string;
    invoiceDate: string | null;
    clNo: number | null;
    prNo: number | null;
    scNo: number | null;
    invoiceAmount: number;
    arDate: string | null;
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    lsNo: number;
    onSuccess: () => void;
}

const VoidInvoicesModal: React.FC<IProps> = ({ isOpen, onClose, lsNo, onSuccess }) => {
    const { get, response: loadResponse, loading } = useFetch();
    const { post, response: voidResponse } = useFetch();
    const [invoices, setInvoices] = useState<IVoidInvoiceDto[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [isVoiding, setIsVoiding] = useState(false);
    const [workflowError, setWorkflowError] = useState<ILeaseRecordsApiError | null>(null);

    useEffect(() => {
        if (isOpen && lsNo) {
            loadVoidableInvoices();
        } else {
            setInvoices([]);
            setSelectedIds(new Set());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, lsNo]);

    const loadVoidableInvoices = async (): Promise<void> => {
        const result = await get(`/v1.0/leases/invoices/voidable?lsNo=${encodeURIComponent(lsNo.toString())}`);
        if (loadResponse.ok && Array.isArray(result)) {
            setInvoices(result.filter((invoice: IVoidInvoiceDto) => !!invoice.arDate));
            // Clear selections when new results arrive
            setSelectedIds(new Set());
        } else {
            setInvoices([]);
        }
    };

    const handleToggleAll = (checked: boolean): void => {
        if (checked) {
            const allIds = new Set(invoices.map((inv) => inv.liTg));
            setSelectedIds(allIds);
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleToggleSingle = (liTg: number): void => {
        setSelectedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(liTg)) {
                newSet.delete(liTg);
            } else {
                newSet.add(liTg);
            }
            return newSet;
        });
    };

    const handleVoidSelected = async (): Promise<void> => {
        if (selectedIds.size === 0) {
            setWorkflowError(parseLeaseRecordsApiError({ title: 'Select at least one invoice to void.' }));
            return;
        }

        if (!window.confirm(`Are you sure you want to void ${selectedIds.size} invoice(s)?`)) {
            return;
        }

        setIsVoiding(true);
        setWorkflowError(null);
        try {
            const payload = { lsNo, liTgs: Array.from(selectedIds) };
            const result = await post('/v1.0/leases/invoices/void-batch', payload);

            if (voidResponse.ok) {
                onSuccess();
                onClose();
            } else {
                setWorkflowError(parseLeaseRecordsApiError(result || voidResponse.data, voidResponse.status));
            }
        } finally {
            setIsVoiding(false);
        }
    };

    const formatCurrency = (amount?: number): string => {
        if (amount === undefined || amount === null) {
            return '';
        }
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };

    const formatDate = (dateStr?: string | null): string => {
        if (!dateStr) {
            return '—';
        }
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US');
    };

    if (!isOpen) {
        return null;
    }

    const allSelected = invoices.length > 0 && selectedIds.size === invoices.length;

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <div>
                        <h3 style={styles.modalTitle}>Void Lease Invoices</h3>
                        <div style={styles.leaseContext}>Lease No. {lsNo}</div>
                    </div>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                {/* Body / Table */}
                <div style={styles.modalBody}>
                    <LeaseRecordsErrorAlert error={workflowError} onDismiss={() => setWorkflowError(null)} />
                    {loading && invoices.length === 0 ? (
                        <div style={styles.loadingState}>
                            <Loader2 className="animate-spin" style={styles.loaderIcon} />
                            <span>Loading voidable invoices...</span>
                        </div>
                    ) : (
                        <div style={styles.tableWrapper}>
                            <table style={styles.tableBase}>
                                <thead style={styles.tableHead}>
                                    <tr>
                                        <th style={{ ...styles.tableHeaderCell, width: '40px', textAlign: 'center' }}>
                                            <LeaseRecordInput
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={(e) => handleToggleAll(e.target.checked)}
                                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                            />
                                        </th>
                                        <th style={styles.tableHeaderCell}>Lease No.</th>
                                        <th style={styles.tableHeaderCell}>Invoice No.</th>
                                        <th style={styles.tableHeaderCell}>Invoice Date</th>
                                        <th style={styles.tableHeaderCell}>Prospect No.</th>
                                        <th style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Invoice Amount</th>
                                        <th style={styles.tableHeaderCell}>A/R Post Date</th>
                                    </tr>
                                </thead>
                                <tbody style={styles.tableBody}>
                                    {invoices.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={styles.noResults}>
                                                No invoices are available to void for this lease.
                                            </td>
                                        </tr>
                                    ) : (
                                        invoices.map((inv) => (
                                            <tr
                                                key={inv.liTg}
                                                style={{
                                                    ...styles.tableRow,
                                                    backgroundColor: selectedIds.has(inv.liTg) ? '#FEF9C3' : '#FFFFFF',
                                                }}
                                            >
                                                <td style={{ ...styles.tableDataCell, textAlign: 'center' }}>
                                                    <LeaseRecordInput
                                                        type="checkbox"
                                                        checked={selectedIds.has(inv.liTg)}
                                                        onChange={() => handleToggleSingle(inv.liTg)}
                                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                    />
                                                </td>
                                                <td style={styles.tableDataCell}>{inv.lsNo}</td>
                                                <td style={styles.tableDataCell}>{inv.invoiceNo}</td>
                                                <td style={styles.tableDataCell}>{formatDate(inv.invoiceDate)}</td>
                                                <td style={styles.tableDataCell}>{inv.prNo || '—'}</td>
                                                <td style={{ ...styles.tableDataCell, textAlign: 'right', fontWeight: 600 }}>
                                                    {formatCurrency(inv.invoiceAmount)}
                                                </td>
                                                <td style={styles.tableDataCell}>{formatDate(inv.arDate)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer Buttons */}
                <div style={styles.modalFooter}>
                    <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto' }}>
                        <button style={styles.buttonSecondary} onClick={onClose}>
                            Back
                        </button>
                        <button
                            style={{
                                ...styles.buttonDanger,
                                opacity: selectedIds.size === 0 || isVoiding ? 0.6 : 1,
                                cursor: selectedIds.size === 0 || isVoiding ? 'not-allowed' : 'pointer',
                            }}
                            onClick={handleVoidSelected}
                            disabled={selectedIds.size === 0 || isVoiding}
                        >
                            {isVoiding ? 'Voiding...' : 'Void'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '90%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: { fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: 0 },
    leaseContext: { marginTop: '0.25rem', color: '#6B7280', fontSize: '0.875rem', fontWeight: 500 },
    closeButton: { color: '#9CA3AF', padding: '0.25rem', borderRadius: '50%', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' },
    closeIcon: { width: '1.25rem', height: '1.25rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' },
    modalFooter: {
        padding: '1rem 1.5rem',
        borderTop: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
        borderBottomLeftRadius: '1rem',
        borderBottomRightRadius: '1rem',
    },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '1rem',
    },
    buttonSecondary: {
        padding: '0.5rem 1.25rem',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonDanger: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#DC2626',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '12.5rem',
        color: '#6B7280',
        gap: '1rem',
    },
    loaderIcon: { width: '2rem', height: '2rem', color: '#4F46E5' },
    tableWrapper: { overflowY: 'auto', flexGrow: 1, borderRadius: '0.5rem', border: '1px solid #E5E7EB', backgroundColor: 'white' },
    tableBase: { minWidth: '100%', borderCollapse: 'collapse' },
    tableHead: { backgroundColor: '#F9FAFB', position: 'sticky', top: 0, zIndex: 1 },
    tableHeaderCell: {
        padding: '0.75rem 1rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #E5E7EB',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    tableRow: { transition: 'background-color 0.1s ease', borderBottom: '1px solid #F3F4F6' },
    tableDataCell: { padding: '0.75rem 1rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#111827' },
    noResults: { padding: '3rem', textAlign: 'center', color: '#6B7280', fontSize: '0.875rem' },
};

export default VoidInvoicesModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/VoidInvoicesModal.tsx